import { http } from '@pendo/http';
const reportsApi = '/api/s/_SID_/report';
const workflowsApi = '/api/s/_SID_/workflow';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';

import { AI_AGENT_REPORT_TYPE } from '@/ai-agents/components/ai-agents.constants';

export function createReport (report) {
    return http.post(reportsApi, report);
}

export function deleteReport (id) {
    return http.delete(`${reportsApi}/${id}`);
}

export function fetchReport (id) {
    return http.get(`${reportsApi}/${id}`);
}

export function fetchReports (type = null) {
    if (!type) return http.get(`${reportsApi}`);

    return http.get(reportsApi, {
        params: {
            type,
        },
    });
}

export function minifyReports (reports) {
    return reports.map((report) => {
        if (['visitor', 'account', 'parentAccount'].includes(report.definition?.kind)) {
            mapSegmentData(report, report);

            return report;
        }
        if (report.type === AI_AGENT_REPORT_TYPE) {
            mapSegmentData(report, { ...report, reportType: report.type });

            return report;
        }

        const reportType = report.type || report.definition.type;
        const minifiedReport = pick(report, [
            'id',
            'rootVersionId',
            'stableVersionId',
            'name',
            'description',
            'kind',
            'type',
            'owner',
            'lastUpdatedByUser',
            'lastUpdatedAt',
            'createdAt',
            'createdByUser',
            'createdByDashboardTemplate',
            'shared',
            'definition.aiAgentId',
        ]);

        const finalReport = { ...minifiedReport, type: reportType };

        if (report.definition?.config?.appId) {
            const { appId } = report.definition.config;

            finalReport.appId = appId;
        } else if (report.definition?.config?.app?.id) {
            const { id: appId } = report.definition.config.app;
            finalReport.appId = appId;
        }

        mapSegmentData(report, finalReport);

        return finalReport;
    });
}

export function updateReport (report, skipVersioning = false) {
    return http.put(`${reportsApi}/${report.id}?skipVersioning=${skipVersioning}`, report);
}

// Util functions
export async function createWorkflow (workflow) {
    const response = await http.post(workflowsApi, mapPermission(workflow));

    try {
        await http.post(`${workflowsApi}/${response.data.id}/apply`, {});
    } catch {
        // Don't worry about errors from the apply endpoint. No need to bubble that up to the user.
    }

    return response.data;
}

export function deleteWorkflow (id) {
    return http.delete(`${workflowsApi}/${id}`);
}

export function fetchAllWorkflowsForSubscription (excludeJourneyDefinition = true) {
    if (excludeJourneyDefinition) {
        return http.get(`${workflowsApi}?excludeJourneyDefinition=true`).then((res) => res.data);
    }

    return http.get(workflowsApi).then((res) => res.data);
}

export function fetchWorkflowForSubscription (id) {
    return http
        .get(`${workflowsApi}/${id}`)
        .then((res) => res.data)
        .catch((err) => {
            console.error('Error fetching workflow', err);

            throw err;
        });
}

export async function updateWorkflow (savedWorkflow, updatedWorkflow, applyToAggregation) {
    const url = `${workflowsApi}/${updatedWorkflow.id}`;
    const response = await http.put(url, mapPermission(updatedWorkflow));
    if (!applyToAggregation) {
        return response.data;
    }
    try {
        // This only needs to be called when the agg params change
        await http.post(`${url}/apply`, {});
    } catch {
        // Don't worry about errors from the apply endpoint. No need to bubble that up to the user.
    }

    return response.data;
}

export function mapPermission (workflow) {
    const result = cloneDeep(workflow);
    result.permission = result.subscriptionSharing.permission;
    delete result.subscriptionSharing;

    return result;
}

export function mapSegmentData (report, finalReport) {
    if (report.definition?.config?.segmentId) {
        finalReport.segmentId = report.definition.config.segmentId;
    } else if (report.definition?.globals?.segments?.value?.id) {
        finalReport.segmentId = report.definition.globals.segments.value.id;
    } else if (report.definition?.config?.segment?.id) {
        finalReport.segmentId = report.definition.config.segment.id;
    }

    if (report.definition?.globals?.segments && isArray(report.definition.globals.segments)) {
        finalReport.segments = report.definition.globals.segments.map((segment) => {
            return {
                id: segment.value.id,
                compound: segment.value.compound,
                name: segment.value.name,
            };
        });
    }
}

export function cancelReport (id) {
    return http.post(`${reportsApi}/${id}/cancel`, {});
}

export function cancelWorkflow (id) {
    return http.post(`${workflowsApi}/${id}/cancel`, {});
}
